/**
 * Project Data Hooks
 *
 * TanStack Query hooks for project CRUD operations.
 * - Automatic caching and deduplication
 * - Optimistic updates for instant UI feedback
 * - WebSocket-driven invalidation for real-time sync
 */

import type { QueryClient } from '@tanstack/react-query';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiResponse, PaginatedResponse } from '@bower/shared';
import { unwrapApiResponse } from '../../services/api/http-client.types';
import { apiService } from '../../services/api.service';
import { createProject, getProject, getProjects } from '../../services/api/projects.api';
import { queryClient, queryKeys } from '../../lib/queryClient';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { useInfinitePaginated } from './useInfinitePaginated';

export interface Project {
  id: string;
  name: string;
  description?: string;
  workspaceId: string;
  /** Set on sub-collections; null/absent at top level. Drives the Type chip. */
  parentProjectId?: string | null;
  tags?: string[];
  /**
   * Creator's user id. Named for what the API actually sends: the Prisma
   * scalar `createdById`. This was declared as `createdBy` — a field no
   * project endpoint returns and no caller read — which is the same
   * phantom-field class that silently blanked the Manage-access dialog.
   */
  createdById: string;
  createdAt: string;
  updatedAt: string;
  noteCount?: number;
  artifactCount?: number;
}

interface CreateProjectData {
  name: string;
  description?: string;
  parentProjectId?: string;
  tags?: string[];
  status?: string;
  /** Create it "Private to me" in the same request (the Share dialog's Just me). */
  isPrivate?: boolean;
  /** Also refuse a sibling whose name differs only in case (409). */
  rejectSimilarName?: boolean;
}

/** What the server made of `isPrivate`: PRIVATE, or RESTRICTED where the
 * workspace keeps owners able to see everything. Absent when not private. */
export type CreatedProject = Project & { privacyMode?: 'PRIVATE' | 'RESTRICTED' };

export interface ProjectListFilters {
  readonly searchTerm?: string;
  readonly parentProjectId?: string | null;
  /**
   * Type chip on the Collections list page. `collection` → top-level only,
   * `sub-collection` → nested only. Omit for "all". Applied server-side via
   * the `collectionType` query param (mutually exclusive with a specific
   * `parentProjectId`, which the subproject rails use).
   */
  readonly collectionType?: 'collection' | 'sub-collection';
  readonly sortBy?: 'name' | 'created' | 'updated';
  readonly sortOrder?: 'asc' | 'desc';
  readonly limit?: number;
  /** See useInfinitePaginated.eagerlyFetchAll — bulk-ops pages that still
   * do client-side filtering pass `true` to auto-load all pages. */
  readonly eagerlyFetchAll?: boolean;
  /** `destination`: rows also carry `canEdit`, `privacyMode` and `parentName`
   * for the caller (the create modal's "Save to..." picker). */
  readonly include?: 'destination';
}

/**
 * The paginated collections list. Drives the Projects list page
 * (and subproject rails on ProjectDetail). Changes to `filters` rotate the
 * queryKey so TanStack starts a fresh query whose first page carries the
 * authoritative `totalCount` — no manual "reset pagination" plumbing in
 * the consuming component.
 */
export function useInfiniteProjects(filters: ProjectListFilters = {}) {
  const workspaceId = useWorkspaceId();

  return useInfinitePaginated<Project>({
    queryKey: [...queryKeys.projects.list(workspaceId), filters] as const,
    fetchPage: async ({ cursor, limit }) => {
      const params: Record<string, string | number> = { limit };
      if (cursor) params.cursor = cursor;
      if (filters.searchTerm) params.search = filters.searchTerm;
      if (filters.sortBy) params.sortBy = filters.sortBy;
      if (filters.sortOrder) params.sortOrder = filters.sortOrder;
      if (filters.include) params.include = filters.include;
      if (filters.parentProjectId === null) {
        // Explicit null means "workspace root" — encode as literal "null"
        // so the backend's string check picks it up.
        params.parentProjectId = 'null';
      } else if (filters.parentProjectId !== undefined) {
        params.parentProjectId = filters.parentProjectId;
      } else if (filters.collectionType) {
        // Type chip — only when no explicit parent is set.
        params.collectionType = filters.collectionType;
      }

      const response = await getProjects(apiService, params);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch projects');
      }
      return response.data as PaginatedResponse<Project>;
    },
    limit: filters.limit,
    enabled: !!workspaceId,
    eagerlyFetchAll: filters.eagerlyFetchAll,
  });
}

/**
 * Fetch a single project by ID
 */
export function useProject(projectId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.projects.detail(projectId || ''),
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID is required');
      const response = await getProject(apiService, projectId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch project');
      }
      return response.data as Project;
    },
    enabled: !!projectId,
    // Seed from any cached list that already has this row so the detail page
    // renders without a loading flash. TanStack will still refetch in the
    // background to fill in any fields the list didn't include.
    placeholderData: () => findCachedProject(queryClient, projectId),
  });
}

/**
 * Imperative, cache-deduped project fetch for non-hook call sites
 * (breadcrumb ancestor walks, parent-name lookups). Shares
 * `queryKeys.projects.detail` with `useProject`, so a project already on
 * screen is answered from cache instead of re-hitting /api/collections/{id}
 * — profiling showed the raw-fetch version tripled that call per
 * collection-detail navigation. Throws on failure, like `useProject`'s
 * queryFn.
 */
export function fetchProjectCached(projectId: string): Promise<Project> {
  return queryClient.fetchQuery({
    queryKey: queryKeys.projects.detail(projectId),
    queryFn: async () => {
      const response = await getProject(apiService, projectId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch project');
      }
      return response.data as Project;
    },
  });
}

// Exported for unit testing. Accepts the QueryClient as a param so tests can
// pass a fresh client without touching the app-level singleton. The flat lists
// it finds are the collection children caches (`queryKeys.projects.children`),
// whose sub-collection rows are full collections, so opening a sub-collection
// from its parent renders at once.
export function findCachedProject(
  qc: QueryClient,
  projectId: string | undefined,
): Project | undefined {
  if (!projectId) return undefined;
  const lists = qc.getQueriesData<Project[]>({ queryKey: ['projects'] });
  for (const [, list] of lists) {
    if (Array.isArray(list)) {
      const match = list.find((p) => p?.id === projectId);
      if (match) return match;
    }
  }
  return undefined;
}

/**
 * Create a new project with optimistic UI update
 */
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    // Not the app's default single retry: a create is not idempotent, so a
    // retried POST after a lost response makes a second collection, and a refusal
    // (a 4xx such as a name clash or a trashed parent) would be sent twice and
    // reach the user a couple of seconds late.
    retry: false,
    // Throws ApiResponseError, so a caller can branch on the code (a 409
    // name clash carries the existing collection's id in `details`).
    mutationFn: async (data: CreateProjectData) =>
      unwrapApiResponse(
        (await createProject(apiService, data)) as ApiResponse<CreatedProject>,
        'Failed to create project',
      ),
    onSuccess: () => {
      // Force hierarchy and recents to refetch immediately
      queryClient.invalidateQueries({ queryKey: ['hierarchy'] });
      queryClient.invalidateQueries({ queryKey: ['recents'] });
    },
  });
}
